import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, CheckSquare, ExternalLink, Gauge, Pause, Play, RotateCcw, SkipBack, SkipForward, Square, Star, Tv2, Volume1, Volume2, X } from "lucide-react";
import { Api } from "../lib/api.js";
import { getBearer } from "../lib/auth.js";
import { playerStore } from "../lib/playerStore.js";
import { getActiveProfileId, setActiveProfileId } from "../lib/profile.js";
import { HlsPlayer } from "../components/HlsPlayer.js";
import { YouTubePlayer, extractYouTubeId } from "../components/YouTubePlayer.js";
import { BookmarkButton } from "../components/BookmarkButton.js";
import { CommentComposer } from "../components/CommentComposer.js";
import { RecommendButton } from "../components/RecommendButton.js";
import { MoreLikeThis } from "../components/MoreLikeThis.js";
import { useNextEpisode, NextEpisodeButton, AutoAdvancePrompt } from "../components/NextEpisode.js";
import { ForcedSubtitleLoader, SubtitlePicker, type SelectedSubtitle } from "../components/SubtitlePicker.js";
import { OutputPicker, getSavedTarget, type OutputTarget } from "../components/OutputPicker.js";
import { SourcePicker, TranscodeChip } from "../components/SourcePicker.js";
import { AudioTrackPicker, TranscodeProgressChip, hlsSourceUrl } from "../components/AudioTrackPicker.js";
import type { AudioTrack, CastMember, ResolvedSource } from "../lib/api.js";
import { CastStrip } from "../components/CastStrip.js";
import { IS_TV } from "../lib/tvMode.js";

/** A cast receiver as reported by GET /api/cast/devices. */
type CastDevice = { device_id: string; label: string; last_seen_ago_s: number; online: boolean };

/** localStorage key for the last TV a cast was sent to (pre-highlighted in the picker). */
const LAST_CAST_TARGET_KEY = "wallace_tv_cast_target";

// Append ?token= when the stream URL is a local proxy endpoint so the server's
// bearer-auth hook can validate it (browser <video> can't send custom headers).
function authenticateStreamUrl(url: string): string {
  if (!url || !url.startsWith("/api/")) return url;
  const token = getBearer();
  if (!token) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

/**
 * Point an HLS session URL at a specific audio track.
 *
 * Switching language is a client-side URL edit, not a re-resolve: the server
 * already returned every track in the release, and each (release, track) pair
 * is its own transcode session, so setting the parameter is all it takes to
 * move to it. Direct streams are returned untouched — there is no track to
 * choose when the browser is demuxing the file itself.
 */
function withAudioTrack(url: string, track: number | null): string {
  if (track === null || !url.includes("/api/hls/session.m3u8")) return url;
  const [path, query = ""] = url.split("?");
  const params = new URLSearchParams(query);
  params.set("audio", String(track));
  return `${path}?${params.toString()}`;
}

/** Start an HLS transcode at the requested position. The player reports
 * timestamps in the original source timeline; HlsPlayer subtracts this offset
 * for the shorter, offset playlist. Direct streams seek natively instead. */
function withStartOffset(url: string, startS: number): string {
  if (startS < 1 || !url.includes("/api/hls/session.m3u8")) return url;
  const [path, query = ""] = url.split("?");
  const params = new URLSearchParams(query);
  params.set("start", String(Math.floor(startS)));
  return `${path}?${params.toString()}`;
}

/**
 * Watch page — now opens on a detail view (backdrop + title + overview +
 * metadata + Play button). The actual player only mounts after the viewer
 * explicitly clicks Play. Resume position is honoured but never auto-starts.
 *
 * In TV kiosk mode (?tv=1 on initial load), autoplay=1 causes the resolved
 * stream URL to be sent to mpv on the Pi instead of playing in the browser.
 */
export function Watch(): JSX.Element {
  // Remount the whole page per (item, cast) pair. State like mpvLaunched must
  // never leak across casts: React Router re-renders /watch/:id in place when
  // a new cast arrives, and a stale mpvLaunched=true silently swallows every
  // later cast's launch POST. The lobby appends &cast=<queued_at> so even a
  // re-cast of the same item gets a fresh page.
  const { id = "" } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  return <WatchInner key={`${id}|${searchParams.get("cast") ?? ""}`} />;
}

function WatchInner(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const autoplay = searchParams.get("autoplay") === "1";
  const decodedId = decodeURIComponent(id);

  const profilesQ = useQuery({ queryKey: ["profiles"], queryFn: Api.profiles.list });
  const [profileId, setProfileId] = useState<string | null>(getActiveProfileId());
  useEffect(() => {
    if (!profileId && profilesQ.data?.profiles[0]) {
      const id = profilesQ.data.profiles[0].id;
      setActiveProfileId(id);
      setProfileId(id);
    }
  }, [profilesQ.data, profileId]);

  // A bare rd:series id is a SHOW, not a playable title — the episode list
  // lives on the series page. Watch only makes sense for a movie or a
  // specific :SxxExx episode id.
  const isBareRdSeries = /^rd:series:tt\d+$/.test(decodedId);
  useEffect(() => {
    if (isBareRdSeries) nav(`/series/${encodeURIComponent(decodedId)}`, { replace: true });
  }, [isBareRdSeries, decodedId, nav]);

  // Keep playingRef in sync so the unmount effect can read it without stale closure.
  const [playing, setPlaying] = useState(false);
  const [showAutoAdvance, setShowAutoAdvance] = useState(false);
  const [subtitle, setSubtitle] = useState<SelectedSubtitle | null>(null);
  const [outputTarget, setOutputTarget] = useState<OutputTarget>(getSavedTarget);
  // Set when the viewer hand-picks a release from SourcePicker; overrides the
  // server's auto-pick for both the browser player and TV casting.
  const [pickedSource, setPickedSource] = useState<ResolvedSource | null>(null);
  // Audio-track override. null = play the track the server chose (English
  // first). Set by the picker; reset whenever the release changes, because
  // track indexes are per-file and mean nothing across releases.
  const [audioTrack, setAudioTrack] = useState<number | null>(null);
  // Where to resume after an audio switch. Switching moves to a different HLS
  // session, which would otherwise restart the film from the beginning.
  const [audioSwitchResumeS, setAudioSwitchResumeS] = useState<number | null>(null);
  // Bandwidth saver: ask the server to re-encode to a light 1080p H.264 HLS
  // session instead of playing the source directly. For networks that can't
  // sustain the source bitrate — progressive playback then stall-cycles on a
  // cache that never gets deep (the 2026-09-09 Fantastic Mr. Fox stalls: an
  // 8.2 Mbit/s file over a path measured at ~8 Mbit/s). Per-device, because
  // bandwidth is a property of the network this device is on, not of the
  // profile; TV casts get the raw URL regardless and ignore it.
  const [bandwidthSaver, setBandwidthSaver] = useState<boolean>(() => {
    try { return localStorage.getItem("wallace-tv:bandwidth-saver") === "1"; } catch { return false; }
  });
  // Navigating to another title (or episode) invalidates both: track indexes
  // only mean anything inside one file.
  useEffect(() => {
    setAudioTrack(null);
    setAudioSwitchResumeS(null);
  }, [decodedId]);

  const [casting, setCasting] = useState(false);
  const [castError, setCastError] = useState<string | null>(null);
  // Multi-TV households: when the server knows more than one receiver, the
  // Send-to-TV click opens this chooser instead of casting blind (previously
  // the server routed to the most-recently-seen TV — a coin flip with two).
  const [castTargets, setCastTargets] = useState<CastDevice[] | null>(null);
  // The device the current cast went to — the remote control (status/command/
  // subtitle) targets the same TV. null = server-default receiver.
  const castDeviceId = useRef<string | null>(null);
  // Remote control state — shown when mpv is actively playing on the TV.
  // We poll mpv/status directly rather than tracking from handlePlay, so the
  // remote appears even if the cast flow had a silent error.
  const [castSent, setCastSent] = useState(false);
  const [remoteActive, setRemoteActive] = useState(false);
  const [remoteStatus, setRemoteStatus] = useState<{ position: number; duration: number; paused: boolean; volume: number } | null>(null);
  // Autoplay-next preference (shared server-side; remote toggles, TV reads).
  const [autoplayNext, setAutoplayNext] = useState(false);
  useEffect(() => {
    fetch("/api/cast/autoplay").then((r) => r.json()).then((d: { enabled?: boolean }) => setAutoplayNext(!!d.enabled)).catch(() => {});
  }, []);
  const toggleAutoplayNext = useCallback(() => {
    setAutoplayNext((prev) => {
      const next = !prev;
      void fetch("/api/cast/autoplay", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: next }),
      }).catch(() => {});
      return next;
    });
  }, []);
  // When mpv is playing a DIFFERENT item than the one in the URL, we show a
  // banner rather than blindly entering remote mode — otherwise the user ends
  // up with controls that look like they belong to this item but actually
  // affect whatever is on the TV.
  const [otherPlayingId, setOtherPlayingId] = useState<string | null>(null);
  const [otherPlayingTitle, setOtherPlayingTitle] = useState<string | null>(null);
  useEffect(() => {
    setPlaying(false);
    setShowAutoAdvance(false);
    setSubtitle(null);
    setCastError(null);
    setCastSent(false);
    setRemoteActive(false);
    setRemoteStatus(null);
    setOtherPlayingId(null);
    setOtherPlayingTitle(null);
  }, [decodedId]);

  // Keep refs in sync with state so the unmount cleanup can read them.
  useEffect(() => { playingRef.current = playing; }, [playing]);

  // Save mini-player state on unmount — lets FloatingMiniPlayer resume from here.
  // Empty deps: only runs on unmount.
  useEffect(() => {
    playerStore.set(null); // clear any prior item when this page loads
    // Exit any active PiP session so the new player takes over cleanly.
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture().catch(() => {});
    }
    return () => {
      if (playingRef.current && lastReportedPos.current > 0 && miniDataRef.current) {
        // Trigger native browser PiP before React removes the video element.
        // Chrome keeps the video playing in the floating PiP window even after
        // the DOM element is unmounted.
        const vid = videoElRef.current;
        if (vid && document.pictureInPictureEnabled && !document.pictureInPictureElement) {
          vid.requestPictureInPicture().catch(() => {});
        }
        playerStore.set({
          itemId: decodedId,
          title: miniDataRef.current.title,
          thumbnail: miniDataRef.current.thumbnail,
          position: Math.floor(lastReportedPos.current),
          duration: Math.floor(durationRef.current),
        });
      } else {
        playerStore.set(null);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On mount (or item change): check what mpv is ACTUALLY playing before
  // entering remote mode.  If it's this item → enter cast mode as normal.
  // If it's a different item → surface a banner so the user can navigate to
  // the right remote instead of accidentally controlling the wrong item.
  useEffect(() => {
    if (IS_TV || outputTarget !== "wallace-tv") return;
    const token = getBearer() ?? "";
    fetch("/api/cast/now-playing", { headers: { "Authorization": `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data: { active: boolean; item_id?: string; title?: string | null }) => {
        if (!data.active || !data.item_id) return;
        if (data.item_id === decodedId) {
          setCastSent(true);
        } else {
          setOtherPlayingId(data.item_id);
          setOtherPlayingTitle(data.title ?? null);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedId]);

  const itemQ = useQuery({
    // outputTarget is part of the key: the server's auto-pick depends on it,
    // so flipping browser/TV has to re-resolve rather than reuse a release
    // chosen for the other one. bandwidthSaver likewise changes the plan
    // (direct progressive vs re-encoded HLS), so it keys the fetch too.
    queryKey: ["item", decodedId, profileId, outputTarget, bandwidthSaver],
    queryFn: () => Api.library.item(decodedId, profileId!, outputTarget === "browser" ? "browser" : "tv", bandwidthSaver),
    enabled: !!profileId,
  });

  // Auto-start when the TV receives a cast command (?autoplay=1).
  // In TV mode: hand the stream URL to mpv instead of the browser player.
  const [mpvLaunched, setMpvLaunched] = useState(false);
  // The URL we told mpv to play — kept so the launch-verification timer can
  // re-fire a dropped POST without relying on query state.
  const launchedUrlRef = useRef<string | null>(null);
  const launchRetriedRef = useRef(false);
  // Mount time — used to ignore cached item data (stale resume position).
  const [mountTs] = useState(() => Date.now());
  useEffect(() => {
    if (!autoplay || !itemQ.data) return;
    // Resume correctness: on TV, autoplay only once the item fetch that
    // started AFTER this page mounted has completed. React Query serves
    // cached data instantly (with the resume position from the previous
    // viewing — or none), refetching in the background; firing on the cache
    // starts playback at a stale position instead of where you left off.
    if (IS_TV && itemQ.dataUpdatedAt < mountTs) return;
    if (IS_TV) {
      const { playback } = itemQ.data;
      const pb = playback as { mode: string; stream_url?: string; external_url?: string };
      let url: string | null = null;
      if (pb.mode === "exoplayer" && pb.stream_url) {
        url = pb.stream_url;
      } else if (pb.mode === "youtube_intent" && pb.external_url) {
        url = pb.external_url;
      }
      if (url && !mpvLaunched) {
        setMpvLaunched(true);
        launchedUrlRef.current = url;
        const token = getBearer() ?? "";
        // Live TV always starts at the live edge — never resume.
        const isLive = itemQ.data?.item?.kind === "live_channel";
        const resumePos = isLive ? 0 : ((itemQ.data?.resume as { position_s?: number } | null)?.position_s ?? 0);
        fetch("/api/cast/mpv", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ url, start_s: resumePos > 10 ? resumePos : 0 }),
        }).catch(() => {/* non-fatal */});
      }
    } else {
      setPlaying(true);
    }
  // eslint-disable name react-hooks/exhaustive-deps
  }, [autoplay, itemQ.data]);

  // Kiosk wedge guard: the command poll lives in TVLobby, so a cast that
  // lands here but can't start (unsupported item, fetch error, dead stream)
  // strands the TV on a dead page with no way home. If mpv hasn't launched
  // within 30 s of an autoplay cast, go back to the lobby and resume polling.
  useEffect(() => {
    if (!IS_TV || !autoplay || mpvLaunched) return;
    const t = window.setTimeout(() => nav("/"), 30_000);
    return () => window.clearTimeout(t);
  }, [IS_TV, autoplay, mpvLaunched, nav]);

  // Launch verification: the launch POST is fire-and-forget — if it's dropped
  // mid-flight (network flap) or mpv can't open the stream, the page would
  // sit on "NOW PLAYING" forever with nothing on screen. Check mpv actually
  // started; re-fire the launch once, then bail to the lobby.
  useEffect(() => {
    if (!IS_TV || !mpvLaunched) return;
    const t = window.setTimeout(() => {
      void (async () => {
        try {
          const r = await fetch("/api/cast/mpv/status");
          const d = (await r.json()) as { active?: boolean };
          if (d?.active) return;
        } catch { /* fall through to retry */ }
        const url = launchedUrlRef.current;
        if (!url || launchRetriedRef.current) { nav("/"); return; }
        launchRetriedRef.current = true;
        const token = getBearer() ?? "";
        await fetch("/api/cast/mpv", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ url, start_s: 0 }),
        }).catch(() => {/* checked below */});
        window.setTimeout(() => {
          void (async () => {
            try {
              const r2 = await fetch("/api/cast/mpv/status");
              const d2 = (await r2.json()) as { active?: boolean };
              if (!d2?.active) nav("/");
            } catch { nav("/"); }
          })();
        }, 10_000);
      })();
    }, 12_000);
    return () => window.clearTimeout(t);
  }, [IS_TV, mpvLaunched, nav]);

  // TV: poll mpv/status until active, then send heartbeats every 5s.
  // This ensures the phone only sees "playing" once mpv is actually playing.
  useEffect(() => {
    if (!IS_TV || !mpvLaunched || !itemQ.data) return;
    let cancelled = false;
    const kind = itemQ.data?.item?.kind ?? "movie";
    const title = itemQ.data?.item?.title ?? null;
    const beat = (): void => {
      fetch("/api/cast/playing-heartbeat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ item_id: decodedId, kind, title }),
      }).catch(() => undefined);
    };
    // TV-side progress writer — the kiosk never sets castSent, so without
    // this the TV (the main playback surface!) records no resume position.
    const postTvProgress = (pos: number, dur: number): void => {
      if (!profileId || !pos) return;
      void fetch("/tv/progress", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getBearer() ?? ""}` },
        body: JSON.stringify({ profile_id: profileId, item_id: decodedId, position_s: Math.floor(pos), duration_s: dur ? Math.floor(dur) : undefined }),
      }).catch(() => {/* non-fatal */});
    };
    const postTvEvent = (ev: "completed" | "abandoned", pos: number, dur: number): void => {
      if (!profileId) return;
      void fetch("/tv/events", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getBearer() ?? ""}` },
        body: JSON.stringify({ profile_id: profileId, item_id: decodedId, event: ev, position_s: Math.floor(pos), duration_s: dur ? Math.floor(dur) : undefined }),
      }).catch(() => {/* non-fatal */});
    };
    // Poll until mpv confirms active, then heartbeat every 5s but ONLY while mpv stays active
    const run = async (): Promise<void> => {
      // Wait up to 20s for mpv to start
      for (let i = 0; i < 20; i++) {
        if (cancelled) return;
        await new Promise(r => setTimeout(r, 1000));
        try {
          const r = await fetch("/api/cast/mpv/status");
          if (r.ok) {
            const d = await r.json() as { active: boolean };
            if (d.active) { beat(); break; }
          }
        } catch { /* retry */ }
      }
      // Keep heartbeating, but stop if mpv goes inactive. Track progress so we
      // can tell a natural end (≈complete) from an early stop.
      let lastPos = 0, lastDur = 0, beats = 0, stallBeats = 0, stallRetried = false;
      while (!cancelled) {
        await new Promise(r => setTimeout(r, 5000));
        if (cancelled) return;
        try {
          const r = await fetch("/api/cast/mpv/status");
          if (r.ok) {
            const d = await r.json() as { active: boolean; paused?: boolean; position?: number; duration?: number };
            if (!d.active) {
              // mpv stopped. Post the final watch event so resume is accurate,
              // then (if it played to near the end and autoplay-next is on)
              // advance; otherwise return to the lobby.
              const reachedEnd = lastDur > 0 && lastPos / lastDur >= 0.9;
              postTvEvent(reachedEnd ? "completed" : "abandoned", lastPos, lastDur);
              if (reachedEnd) {
                const ap = await fetch("/api/cast/autoplay").then(x => x.json()).then((x: { enabled?: boolean }) => !!x.enabled).catch(() => false);
                const nxt = nextEpisodeRef.current;
                if (ap && nxt) { nav(`/watch/${encodeURIComponent(nxt.id)}?autoplay=1`); return; }
              }
              nav("/");
              return;
            }
            const prevPos = lastPos;
            lastPos = d.position ?? lastPos;
            lastDur = d.duration ?? lastDur;
            // Provider-stall recovery (2026-09-20: a starlite node 404'd a
            // segment mid-playlist and mpv sat in a buffering-retry loop on a
            // frozen frame for an hour — active:true, position hard-frozen,
            // and the kiosk stranded because nobody pressed stop). Count a
            // stall only when position is genuinely frozen (jittery progress
            // still clears it — a transcode edge-kiss advances in bursts).
            // A backward jump means a relaunch happened — reset, don't count.
            // Live channels have no position — never stall-checked.
            const isLive = kind === "live_channel";
            if (!isLive && !d.paused && lastDur >= 0) {
              if (lastPos < prevPos - 30) stallBeats = 0;
              else if (lastPos - prevPos < 1.5) {
                stallBeats += 1;
                // Surface the stall on the TV itself via mpv's OSD — a frozen
                // frame with no overlay reads as a dead TV (Douglas, 2026-10-29).
                // Re-posted each frozen beat so it stays up until progress resumes.
                if (stallBeats >= 2) {
                  void fetch("/api/cast/mpv/command", {
                    method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getBearer() ?? ""}` },
                    body: JSON.stringify({ action: "show_text", text: `Buffering… ${stallBeats * 5}s`, value: 6000 }),
                  }).catch(() => undefined);
                }
              }
              else stallBeats = 0;
              if (stallBeats >= 9 && !stallRetried) {
                // ~45 s frozen: stop, re-plan fresh (new CDN node / new
                // release ranking), relaunch once from the top.
                stallRetried = true; stallBeats = 0; lastPos = 0;
                void fetch("/api/cast/mpv/command", {
                  method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getBearer() ?? ""}` },
                  body: JSON.stringify({ action: "show_text", text: "Source stalled — restarting…", value: 10000 }),
                }).catch(() => undefined);
                try {
                  await fetch("/api/cast/mpv/command", {
                    method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getBearer() ?? ""}` },
                    body: JSON.stringify({ action: "stop" }),
                  });
                  const fresh = await itemQ.refetch();
                  const pb = (fresh.data?.playback ?? null) as { mode?: string; stream_url?: string } | null;
                  if (pb?.mode === "exoplayer" && pb.stream_url) {
                    launchedUrlRef.current = pb.stream_url;
                    await fetch("/api/cast/mpv", {
                      method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getBearer() ?? ""}` },
                      body: JSON.stringify({ url: pb.stream_url, start_s: 0 }),
                    });
                    continue;
                  }
                } catch { /* fall through to bail */ }
                // No fresh URL — treat as unrecoverable and bail below.
                stallBeats = 9;
              }
              if (stallBeats >= 9 && stallRetried) {
                // Stalled again after the one recovery — the source is
                // broken, not unlucky. Leave a reason on the lobby.
                postTvEvent("abandoned", lastPos, lastDur);
                try { sessionStorage.setItem("wt_watch_error", `${itemQ.data?.item?.title ?? "That"} kept stalling — the source looks broken, pick another`); } catch { /* */ }
                nav("/");
                return;
              }
            }
            // Post playback progress every ~30 s so resume-where-you-left-off
            // works for TV playback (the castSent-gated poll only covers the phone).
            beats += 1;
            if (beats % 6 === 0 && lastPos > 0) postTvProgress(lastPos, lastDur);
          }
        } catch { /* network blip — keep going */ }
        if (!cancelled) beat();
      }
    };
    void run();
    return () => {
      cancelled = true;
      fetch("/api/cast/playing-heartbeat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ item_id: decodedId, kind, title: null, stopped: true }),
      }).catch(() => undefined);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IS_TV, mpvLaunched, decodedId]);

  // Phone: poll live now-playing state; show controls only when TV is actually playing
  const [liveActive, setLiveActive] = useState(false);
  const [liveItemId, setLiveItemId] = useState<string | null>(null);
  useEffect(() => {
    if (IS_TV || !castSent) return;
    const token = getBearer() ?? "";
    const poll = (): void => {
      fetch("/api/cast/now-playing", { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json() as Promise<{ active: boolean; item_id?: string }>)
        .then(d => { setLiveActive(d.active); setLiveItemId(d.item_id ?? null); })
        .catch(() => undefined);
    };
    poll();
    const t = window.setInterval(poll, 3_000);
    return () => window.clearInterval(t);
  }, [castSent]);

  const event = useMutation({ mutationFn: Api.library.event });

  const viewedReported = useRef(false);
  const completedReported = useRef(false);
  const lastReportedPos = useRef(0);
  const lastProgressAt = useRef(0);
  const durationRef = useRef(0);
  const playingRef = useRef(false);
  const miniDataRef = useRef<{ title: string; thumbnail: string | null } | null>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);

  const reportEvent = (
    ev: "viewed" | "completed" | "abandoned",
    position_s?: number,
    duration_s?: number,
  ): void => {
    if (!profileId) return;
    event.mutate({ profile_id: profileId, item_id: decodedId, event: ev, position_s, duration_s });
  };

  // Flipping the saver re-resolves the item (it's in the query key), which
  // swaps the playback URL and re-mounts the player — carry the position
  // across, same as an audio-track switch.
  function toggleBandwidthSaver(): void {
    const next = !bandwidthSaver;
    try { localStorage.setItem("wallace-tv:bandwidth-saver", next ? "1" : "0"); } catch { /* private mode */ }
    setAudioSwitchResumeS(Math.floor(lastReportedPos.current) || null);
    setBandwidthSaver(next);
  }

  const handleTimeUpdate = (pos: number, dur: number): void => {
    lastReportedPos.current = pos;
    durationRef.current = dur;
    if (!completedReported.current && dur > 0 && pos / dur >= 0.9) {
      completedReported.current = true;
      reportEvent("completed", Math.floor(pos), Math.floor(dur));
      return;
    }
    const now = Date.now();
    if (profileId && pos > 5 && now - lastProgressAt.current > 15_000) {
      lastProgressAt.current = now;
      Api.library.progress({
        profile_id: profileId,
        item_id: decodedId,
        position_s: Math.floor(pos),
        duration_s: dur > 0 ? Math.floor(dur) : undefined,
      }).catch(() => { /* non-fatal */ });
    }
  };

  useEffect(() => {
    const onUnload = () => {
      if (!completedReported.current && viewedReported.current && lastReportedPos.current > 0) {
        const token = localStorage.getItem("wallace-tv:bearer") ?? "";
        const body = JSON.stringify({
          profile_id: profileId,
          item_id: decodedId,
          event: "abandoned",
          position_s: Math.floor(lastReportedPos.current),
        });
        navigator.sendBeacon?.(
          `/tv/events?token=${encodeURIComponent(token)}`,
          new Blob([body], { type: "application/json" }),
        );
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [profileId, decodedId]);

  // Next-episode lookup — must run BEFORE any early return so the hook order
  // stays stable across renders (React #310 otherwise). The hook's internal
  // queries gate on kind/category, so safe to call with empty placeholders
  // while itemQ is still loading.
  const itemForNext = itemQ.data?.item;
  const nextEpisode = useNextEpisode(
    {
      id: decodedId,
      kind: itemForNext?.kind ?? "",
      category: itemForNext?.category ?? null,
    },
    profileId,
  );
  // Latest nextEpisode for the TV-side auto-advance effect (avoids stale closure).
  const nextEpisodeRef = useRef(nextEpisode);
  nextEpisodeRef.current = nextEpisode;

  // Remote control — must also be before early returns (rules of hooks).
  // Targets the TV the cast actually went to (multi-TV).
  const mpvCommand = useCallback(async (action: string, value?: number) => {
    const token = getBearer() ?? "";
    await fetch("/api/cast/mpv/command", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ action, value, ...(castDeviceId.current ? { device_id: castDeviceId.current } : {}) }),
    });
  }, []);

  // Sync the chosen subtitle to the TV's mpv. Fires when the selection changes
  // (manual or auto-selected) and once the TV's player goes live — so the
  // subtitle the remote picked actually shows on the TV. No-op for in-browser
  // playback (only runs while a cast is active).
  const subtitleFileId = subtitle?.file_id ?? null;
  useEffect(() => {
    if (!remoteActive) return;
    const token = getBearer() ?? "";
    void fetch("/api/cast/mpv/subtitle", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ file_id: subtitleFileId, token, ...(castDeviceId.current ? { device_id: castDeviceId.current } : {}) }),
    }).catch(() => {/* non-fatal */});
  }, [remoteActive, subtitleFileId]);

  const lastProgressPost = useRef(0);
  useEffect(() => {
    if (!castSent) return;
    const token = getBearer() ?? "";
    const postProgress = (pos: number, dur: number): void => {
      if (!profileId || !pos) return;
      void fetch("/tv/progress", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ profile_id: profileId, item_id: decodedId, position_s: Math.floor(pos), duration_s: dur ? Math.floor(dur) : undefined }),
      }).catch(() => {/* non-fatal */});
    };
    const postEvent = (ev: "viewed" | "completed" | "abandoned", pos: number, dur: number): void => {
      if (!profileId) return;
      void fetch("/tv/events", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ profile_id: profileId, item_id: decodedId, event: ev, position_s: Math.floor(pos), duration_s: dur ? Math.floor(dur) : undefined }),
      }).catch(() => {/* non-fatal */});
    };
    const poll = async (): Promise<void> => {
      try {
        const r = await fetch(`/api/cast/mpv/status${castDeviceId.current ? `?device_id=${encodeURIComponent(castDeviceId.current)}` : ""}`, {
          headers: { "Authorization": `Bearer ${token}` },
        });
        const data = await r.json() as { active: boolean; position?: number; duration?: number; paused?: boolean; volume?: number };
        if (data.active) {
          const pos = data.position ?? 0;
          const dur = data.duration ?? 0;
          setRemoteActive(true);
          setRemoteStatus({ position: pos, duration: dur, paused: data.paused ?? false, volume: data.volume ?? 100 });
          // Post progress every 30 s.
          if (pos > 0 && Date.now() - lastProgressPost.current > 30_000) {
            lastProgressPost.current = Date.now();
            postProgress(pos, dur);
          }
        } else if (remoteActive) {
          // mpv stopped — record final event and exit remote mode.
          const pos = remoteStatus?.position ?? 0;
          const dur = remoteStatus?.duration ?? 0;
          const pct = dur > 0 ? pos / dur : 0;
          postEvent(pct >= 0.85 ? "completed" : pos > 30 ? "viewed" : "abandoned", pos, dur);
          setRemoteActive(false);
          setCastSent(false);
        }
      } catch { /* network blip */ }
    };
    void poll();
    const t = window.setInterval(() => void poll(), 3000);
    return () => window.clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [castSent, remoteActive]);

  // TV kiosk: auto-return to lobby when mpv goes genuinely idle.
  // Waits 8s before first check (startup grace), requires 4 consecutive
  // inactive responses (~16s) before navigating, and cleans up properly.
  const mpvInactiveCount = useRef(0);
  const mpvPollInterval = useRef<number | null>(null);
  useEffect(() => {
    if (!IS_TV || !mpvLaunched) return;
    mpvInactiveCount.current = 0;
    // Delay first poll to let mpv open the stream
    const startDelay = window.setTimeout(() => {
      mpvPollInterval.current = window.setInterval(async () => {
        try {
          const r = await fetch("/api/cast/mpv/status"); // public — no token needed on TV
          if (!r.ok) return; // auth error or server error — don't count as inactive
          const d = await r.json() as { active: boolean };
          if (d.active) {
            mpvInactiveCount.current = 0;
          } else {
            mpvInactiveCount.current += 1;
            if (mpvInactiveCount.current >= 4) nav("/");
          }
        } catch { /* network blip — don't count */ }
      }, 4000);
    }, 8000);
    return () => {
      window.clearTimeout(startDelay);
      if (mpvPollInterval.current) window.clearInterval(mpvPollInterval.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpvLaunched]);

  if (!profileId) {
    return (
      <div className="px-10 py-10">
        <div className="card p-8">
          <div className="section-label">No profile selected</div>
          <p className="text-parchment mt-2">Create a profile first, then come back.</p>
          <Link to="/profiles" className="pill mt-4 inline-flex">Go to profiles</Link>
        </div>
      </div>
    );
  }

  if (itemQ.isLoading) {
    return (
      <div className="px-10 py-10">
        <div className="skeleton h-[480px] w-full" />
      </div>
    );
  }

  if (itemQ.isError || !itemQ.data) {
    return (
      <div className="px-10 py-10">
        <div className="card p-8 text-vermilionHi">
          Couldn't load this item. <Link to="/library" className="text-gold underline">Back to library</Link>
        </div>
      </div>
    );
  }

  const { item, playback, resume } = itemQ.data;
  const isYouTube = playback.mode === "youtube_intent";
  const ytId = isYouTube && playback.mode === "youtube_intent" ? extractYouTubeId(playback.external_url) : null;
  // Live TV is always live — no resume offer or seek-to-position.
  const resumePosition = item.kind !== "live_channel" && resume && resume.last_event !== "completed" ? resume.position_s : 0;
  // Stream validation status — server pre-probes the top 3 candidate URLs
  // and tells us whether at least one returned 200/206 before handing back
  // the playable URL. Used to render the green ✓ / amber ⚠ chip next to Play.
  const playback2 = playback as unknown as {
    mode: string;
    stream_url?: string;
    external_url?: string;
    validated?: boolean;
    resolved_by?: "real-debrid" | "premiumize" | "alldebrid" | "comet" | null;
    probed?: number;
    probed_ok?: number;
    needs_transcode?: boolean;
    via_hls?: boolean;
    audio_codec?: string | null;
    audio_tracks?: AudioTrack[];
    audio_track?: number | null;
    transcode_reason?: string | null;
    video_codec?: string | null;
    video_playable_in_browser?: boolean;
    probed_duration_s?: number | null;
    src_id?: string | null;
    source_count?: number;
    debrid_error?: string | null;
  };

  // A hand-picked release wins over the server's auto-pick. `play_url` is the
  // browser-playable one (transcode proxy when the audio needs it); the raw
  // `stream_url` is what gets cast to mpv, which decodes Dolby natively.
  // Every audio track in whatever release is playing, and which one is mapped.
  // A hand-picked source carries its own list — a different release has a
  // different track layout, so it replaces the auto-pick's wholesale.
  const videoCodec = pickedSource?.video_codec ?? playback2.video_codec ?? null;
  const videoPlayableInBrowser =
    pickedSource?.video_playable_in_browser ?? playback2.video_playable_in_browser ?? true;
  const audioTracks = pickedSource?.audio_tracks ?? playback2.audio_tracks ?? [];
  const activeAudioTrack = audioTrack ?? pickedSource?.audio_track ?? playback2.audio_track ?? null;
  // Language of the audio actually playing — this, not the film's original
  // language, is what decides whether full subtitles are wanted.
  const audioLanguage = audioTracks.find((t) => t.index === activeAudioTrack)?.language ?? null;

  const requestedStartS = audioSwitchResumeS ?? resumePosition;
  const effectivePlayback = pickedSource
    ? { mode: "exoplayer" as const, stream_url: withStartOffset(withAudioTrack(pickedSource.play_url, audioTrack), requestedStartS) }
    : playback.mode === "exoplayer"
      ? { ...playback, stream_url: withStartOffset(withAudioTrack(playback.stream_url, audioTrack), requestedStartS) }
      : playback;
  const castStreamUrl = pickedSource?.stream_url ?? item.stream_url ?? null;
  const activeSrcId = pickedSource?.src_id ?? playback2.src_id ?? null;
  const needsTranscode = pickedSource?.needs_transcode ?? playback2.needs_transcode ?? false;
  const audioCodec = pickedSource?.audio_codec ?? playback2.audio_codec ?? null;

  const validation = !isYouTube && playback2.stream_url
    ? {
        validated: pickedSource?.validated ?? playback2.validated ?? null,
        resolved_by: (pickedSource?.resolved_by as typeof playback2.resolved_by) ?? playback2.resolved_by ?? null,
        probed: playback2.probed ?? 0,
        probed_ok: playback2.probed_ok ?? 0,
      }
    : null;

  function openOnYouTube(): void {
    if (playback.mode !== "youtube_intent") return;
    window.open(playback.external_url, "_blank", "noopener");
  }

  async function handlePlay(chosenDeviceId?: string): Promise<void> {
    // Play locally only when explicitly set to browser, or YouTube on browser output
    if (outputTarget === "browser") {
      setPlaying(true);
      return;
    }
    if (!profileId) return;
    setCasting(true);
    setCastError(null);
    try {
      // Multi-TV gate — covers BOTH cast paths (item queue and raw mpv).
      // With more than one known receiver, ask which TV first; otherwise the
      // server routes to a default, a coin flip in a two-TV home. A picker
      // failure must never break casting: any error falls through to the
      // server default.
      if (!chosenDeviceId) {
        try {
          const { devices } = await Api.cast.devices();
          if (devices.length > 1) {
            const lastUsed = localStorage.getItem(LAST_CAST_TARGET_KEY);
            setCastTargets(
              [...devices].sort((a, b) =>
                Number(b.online) - Number(a.online)
                || (a.device_id === lastUsed ? -1 : b.device_id === lastUsed ? 1 : 0)
                || a.last_seen_ago_s - b.last_seen_ago_s),
            );
            setCasting(false);
            return;
          }
        } catch { /* picker unavailable — cast blind as before */ }
      }
      // A hand-picked release has to go over the raw-URL path. The item_id
      // path makes the TV resolve the stream itself, which would just re-run
      // the auto-pick and silently discard the choice.
      if (pickedSource) {
        const res = await Api.cast.mpv({
          url: pickedSource.stream_url,
          ...(resumePosition > 0 ? { start_s: resumePosition } : {}),
          sub_file_id: subtitle?.file_id ?? null,
          ...(chosenDeviceId ? { device_id: chosenDeviceId } : {}),
        });
        if (!res.ok) throw new Error(res.error ?? "Cast failed");
        if (chosenDeviceId) {
          localStorage.setItem(LAST_CAST_TARGET_KEY, chosenDeviceId);
          castDeviceId.current = chosenDeviceId;
        }
        setCastTargets(null);
        setCasting(false);
        setCastSent(true);
        return;
      }
      // The server queues the command and the TV pops it on its next poll
      // (~2.5 s) — no "offline" gate; sending always works.
      const res = await Api.cast.play({
        item_id: decodedId,
        kind: item.kind,
        profile_id: profileId,
        title: item.title,
        ...(chosenDeviceId ? { device_id: chosenDeviceId } : {}),
      });
      if (!res.ok) throw new Error(res.error ?? "Cast failed");
      if (chosenDeviceId) localStorage.setItem(LAST_CAST_TARGET_KEY, chosenDeviceId);
      castDeviceId.current = chosenDeviceId ?? res.device_id ?? null;
      setCastTargets(null);
      setCasting(false);
      setCastSent(true);
      if (res.tv_online === false) {
        setCastError(
          `Sent to ${res.label ?? "the TV"} — it hasn't checked in for a while, so playback starts when it's back online.`,
        );
      }
    } catch (err) {
      setCastError((err as Error).message);
      setCasting(false);
    }
  }

  function handlePickCastTarget(deviceId: string): void {
    setCastTargets(null);
    void handlePlay(deviceId);
  }

  // TMDB-enriched fields, when available, take priority over the M3U title.
  const item2 = item as typeof item & {
    tmdb_overview?: string | null;
    tmdb_backdrop_url?: string | null;
    tmdb_genres?: string[] | null;
    tmdb_rating?: number | null;
    tmdb_runtime_min?: number | null;
    tmdb_release_date?: string | null;
    tmdb_poster_url?: string | null;
    cast?: CastMember[];
    director?: string | null;
    guest_stars?: CastMember[];
    tmdb_cast_top?: string[] | null;
    tmdb_director?: string | null;
  };
  const backdrop = item2.tmdb_backdrop_url ?? item.thumbnail_url;
  const poster = item2.tmdb_poster_url ?? item.thumbnail_url;

  // Keep mini-player data ref current so unmount cleanup has it.
  miniDataRef.current = { title: item.title, thumbnail: poster ?? backdrop ?? null };
  const overview = item2.tmdb_overview ?? item.description ?? null;

  // Derive IMDb id + season/episode for the subtitle picker. rd: items carry
  // it in the id; local items expose it via tvg_id.
  const rdParts = /^rd:(movie|series):(tt\d+)(?::S(\d{1,3})E(\d{1,3}))?$/.exec(decodedId);
  const item3 = item as typeof item & {
    tvg_id?: string | null;
    season_number?: number | null;
    episode_number?: number | null;
  };
  const subtitleImdb: string | null = rdParts
    ? rdParts[2]!
    : (item3.tvg_id && /^tt\d+$/.test(item3.tvg_id) ? item3.tvg_id : null);
  const subtitleKind: "movie" | "series" = rdParts
    ? (rdParts[1] === "series" ? "series" : "movie")
    : (item.kind === "tv_show" ? "series" : "movie");
  const subtitleSeason: number | undefined = rdParts && rdParts[3]
    ? Number(rdParts[3])
    : (item3.season_number ?? undefined);
  const subtitleEpisode: number | undefined = rdParts && rdParts[4]
    ? Number(rdParts[4])
    : (item3.episode_number ?? undefined);
  const rating = typeof item2.tmdb_rating === "number" ? Math.round(item2.tmdb_rating * 10) / 10 : null;
  const genres = (item2.tmdb_genres ?? []).slice(0, 4);
  const runtime = item2.tmdb_runtime_min ?? null;
  const releaseYear = item2.tmdb_release_date
    ? new Date(item2.tmdb_release_date).getFullYear()
    : null;

  // Principal cast: rd: items carry live TMDB credits (photos); library items
  // carry name-only lists from the credits backfill.
  const castList: CastMember[] =
    item2.cast ?? (item2.tmdb_cast_top ?? []).slice(0, 8).map((name) => ({ name }));
  const directorLine = item2.director ?? item2.tmdb_director ?? null;
  const directorLabel = item.kind === "tv_show" ? "Created by" : "Directed by";
  const guestStars = item2.guest_stars ?? [];

  // Sent but mpv not yet confirmed — show a brief waiting state.
  if (castSent && !remoteActive) {
    if (!liveActive) {
      // Sent but not yet confirmed playing — show spinner, back button
      return (
        <div className="min-h-screen bg-lacquer-deep flex flex-col items-center justify-center gap-4" style={{ fontFamily: "'Cinzel', serif" }}>
          <div className="section-label text-gold tracking-[0.4em] text-xs">SENDING TO TV</div>
          <div className="flex gap-1.5">
            {[0,1,2].map(i => <span key={i} className="block w-1.5 h-1.5 rounded-full bg-gold/60 animate-pulse" style={{ animationDelay: `${i*300}ms` }} />)}
          </div>
          <p className="text-parchment/40 text-xs mt-2">Waiting for TV to start…</p>
          <button className="pill-ghost mt-4 text-xs" onClick={() => { setCastSent(false); nav(-1); }}>← Back</button>
        </div>
      );
    }
    // TV is confirmed playing — show controls
    return (
      <div className="min-h-screen bg-lacquer-deep flex flex-col" style={{ fontFamily: "'Cinzel', serif" }}>
        {backdrop && (
          <div className="absolute inset-0 overflow-hidden">
            <img src={backdrop} alt="" className="w-full h-full object-cover opacity-20" referrerPolicy="no-referrer" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(10,4,4,1) 30%, rgba(10,4,4,0.7) 100%)" }} />
          </div>
        )}
        {/* Top bar — exit without stopping */}
        <div className="relative z-10 flex items-center justify-between px-4 pt-4">
          <button
            className="pill bg-parchment/10 border-parchment/20 text-ivory text-sm font-display tracking-smallcaps"
            onClick={() => nav("/")}
          >
            ← Browse
          </button>
          <button
            className="pill-ghost text-vermilionHi text-xs"
            onClick={async () => { await mpvCommand("stop"); setCastSent(false); nav("/"); }}
          >
            ■ Stop
          </button>
        </div>
        {/* Controls */}
        <div className="relative z-10 flex flex-col flex-1 items-center justify-center gap-6 px-6 py-6 text-center">
          <div className="section-label text-gold tracking-[0.4em] text-xs">NOW PLAYING ON TV</div>
          {poster && <img src={poster} alt={item.title} className="w-28 rounded-lg shadow-xl opacity-90" referrerPolicy="no-referrer" />}
          <div>
            <h2 className="font-display text-ivory text-xl" style={{ letterSpacing: "0.05em" }}>{item.title}</h2>
            {item2.tmdb_genres && item2.tmdb_genres.length > 0 && (
              <p className="text-parchment/50 text-xs mt-1">{item2.tmdb_genres.slice(0, 3).join(" · ")}</p>
            )}
          </div>
          <div className="flex flex-col items-center gap-4 w-full max-w-xs">
            <div className="flex gap-4 items-center">
              <button className="pill-ghost text-2xl px-5 py-3" onClick={() => mpvCommand("seek_back", 10)} title="Back 10s">⏪</button>
              <button className="pill-ghost text-2xl px-6 py-3" onClick={() => mpvCommand("pause")} title="Pause/Play">⏯</button>
              <button className="pill-ghost text-2xl px-5 py-3" onClick={() => mpvCommand("seek_fwd", 30)} title="Forward 30s">⏩</button>
            </div>
            <div className="flex gap-3 items-center">
              <button className="pill-ghost text-xl px-4 py-2" onClick={() => mpvCommand("volume_down", 10)}>🔉</button>
              <button className="pill-ghost text-xl px-4 py-2" onClick={() => mpvCommand("volume_up", 10)}>🔊</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Phone remote-control mode — shown after successfully casting to the TV.
  if (remoteActive) {
    const pos = remoteStatus?.position ?? 0;
    const dur = remoteStatus?.duration ?? 0;
    const paused = remoteStatus?.paused ?? false;
    const volume = remoteStatus?.volume ?? 100;
    const progress = dur > 0 ? (pos / dur) * 100 : 0;
    return (
      <div className="min-h-screen bg-lacquer-deep flex flex-col" style={{ fontFamily: "'Cinzel', serif" }}>
        {/* Backdrop */}
        {backdrop && (
          <div className="absolute inset-0 overflow-hidden">
            <img src={backdrop} alt="" className="w-full h-full object-cover opacity-20" referrerPolicy="no-referrer" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(10,4,4,1) 30%, rgba(10,4,4,0.7) 100%)" }} />
          </div>
        )}
        <div className="relative z-10 flex flex-col flex-1 items-center justify-center gap-8 px-6 py-10 text-center">
          <div>
            <div className="section-label text-vermilion tracking-[0.4em] text-xs mb-2">PLAYING ON TV</div>
            <h1 className="font-display text-ivory text-3xl leading-tight" style={{ letterSpacing: "0.06em" }}>{item.title}</h1>
          </div>

          {/* Progress bar — tap to seek */}
          <div className="w-full max-w-sm">
            <div
              className="h-4 flex items-center cursor-pointer group"
              onPointerDown={(e) => {
                if (!dur) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                void mpvCommand("seek_to", Math.floor(pct * dur));
              }}
            >
              <div className="h-1 group-active:h-2 bg-white/10 rounded-full overflow-hidden w-full transition-all">
                <div className="h-full bg-vermilion rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
            <div className="flex justify-between text-parchment/50 text-xs mt-1 font-mono">
              <span>{fmtTime(Math.floor(pos))}</span>
              {dur > 0 && <span>{fmtTime(Math.floor(dur))}</span>}
            </div>
          </div>

          {/* Controls — large touch targets */}
          <div className="flex items-center gap-8">
            <button
              onPointerDown={() => void mpvCommand("seek_back", 10)}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl active:bg-white/10 text-parchment/80 transition-colors"
            >
              <SkipBack size={36} />
              <span className="text-xs tracking-widest">10s</span>
            </button>

            <button
              onPointerDown={() => void mpvCommand("pause")}
              className="w-24 h-24 rounded-full bg-vermilion border-2 border-goldHi flex items-center justify-center text-ivory active:scale-95 transition-transform shadow-2xl"
            >
              {paused ? <Play size={40} /> : <Pause size={40} />}
            </button>

            <button
              onPointerDown={() => void mpvCommand("seek_fwd", 30)}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl active:bg-white/10 text-parchment/80 transition-colors"
            >
              <SkipForward size={36} />
              <span className="text-xs tracking-widest">30s</span>
            </button>
          </div>

          {/* Volume */}
          <div className="flex items-center gap-4">
            <button
              onPointerDown={() => void mpvCommand("volume_down", 10)}
              className="flex flex-col items-center gap-1 p-3 rounded-2xl active:bg-white/10 text-parchment/70 transition-colors"
            >
              <Volume1 size={28} />
              <span className="text-xs tracking-widest">VOL−</span>
            </button>
            <div className="text-parchment/40 text-sm font-mono w-12 text-center">{Math.round(volume)}%</div>
            <button
              onPointerDown={() => void mpvCommand("volume_up", 10)}
              className="flex flex-col items-center gap-1 p-3 rounded-2xl active:bg-white/10 text-parchment/70 transition-colors"
            >
              <Volume2 size={28} />
              <span className="text-xs tracking-widest">VOL+</span>
            </button>
          </div>

          {/* Subtitles + autoplay — adjustable while the remote is front & centre */}
          <div className="flex flex-col items-center gap-3 w-full max-w-xs">
            {!isYouTube && (
              <SubtitlePicker
                imdbId={subtitleImdb}
                kind={subtitleKind}
                season={subtitleSeason}
                episode={subtitleEpisode}
                selected={subtitle}
                onSelect={setSubtitle}
                originalLanguage={item2.original_language ?? null}
                audioLanguage={audioLanguage}
              />
            )}
            <button
              onClick={toggleAutoplayNext}
              className={`pill-ghost text-sm ${autoplayNext ? "text-goldHi" : "text-parchment/70"}`}
              title="Automatically play the next episode (or a related YouTube video) when this one ends"
            >
              {autoplayNext ? <CheckSquare size={16} /> : <Square size={16} />}
              Autoplay next
            </button>
          </div>

          <button
            onPointerDown={async () => {
              await mpvCommand("stop");
              setRemoteActive(false);
              setCastSent(false);
            }}
            className="flex items-center gap-2 px-8 py-4 rounded-full border border-hairline text-parchment/60 text-sm active:bg-white/10 transition-colors"
          >
            <Square size={16} /> Stop
          </button>
        </div>
      </div>
    );
  }

  // TV mode: show a minimal "Now Playing" screen while mpv has the display.
  if (IS_TV && mpvLaunched) {
    const item = itemQ.data?.item;
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center bg-lacquer-deep select-none"
        style={{ fontFamily: "'Cinzel', serif" }}
      >
        <div className="flex flex-col items-center gap-6 text-center px-8">
          <div className="section-label text-gold tracking-[0.4em] text-sm">NOW PLAYING</div>
          <h1 className="font-display text-ivory text-4xl leading-tight" style={{ letterSpacing: "0.06em" }}>
            {item?.title ?? "…"}
          </h1>
          <div className="flex gap-1.5 mt-2">
            {[0, 1, 2].map((i) => (
              <span key={i} className="block w-1.5 h-1.5 rounded-full bg-gold/60 animate-pulse"
                style={{ animationDelay: `${i * 300}ms` }} />
            ))}
          </div>
          <button
            className="pill-ghost mt-8 text-parchment/60 text-xs uppercase tracking-widest"
            onClick={() => {
              fetch("/api/cast/mpv/stop", { method: "POST" }).catch(() => {/* */});
            }}
          >
            ← Stop &amp; go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-6">
      <button onClick={() => nav(-1)} className="pill-ghost mb-4">
        <ArrowLeft size={14} /> Back
      </button>

      {otherPlayingId && !castSent && !remoteActive && (
        <Link
          to={`/watch/${encodeURIComponent(otherPlayingId)}`}
          className="w-full mb-4 px-4 py-3 rounded-xl bg-vermilion/15 border border-vermilion/30 hover:bg-vermilion/25 transition-colors flex items-center gap-3"
        >
          <Tv2 size={16} className="text-vermilion flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-vermilion/70 uppercase tracking-widest font-display leading-none mb-0.5">Now Playing on TV</div>
            <div className="text-ivory text-sm truncate">{otherPlayingTitle ?? "Something is playing"}</div>
          </div>
          <span className="text-parchment/60 text-xs">Go to remote →</span>
          <span className="block w-2 h-2 rounded-full bg-vermilion flex-shrink-0 animate-pulse" />
        </Link>
      )}

      {playing ? (
        <div className="relative">
          {/* PiP / browse button for local playback */}
          {!IS_TV && document.pictureInPictureEnabled && (
            <button
              className="pill-ghost absolute top-2 right-2 z-20 text-xs bg-black/40"
              title="Browse while playing (Picture-in-Picture)"
              onClick={() => {
                const vid = videoElRef.current;
                if (vid) vid.requestPictureInPicture().catch(() => {});
                nav("/");
              }}
            >
              ⧉ Browse
            </button>
          )}
          <PlayerArea
            isYouTube={isYouTube}
            ytId={ytId}
            playback={effectivePlayback}
            poster={poster}
            resumePosition={requestedStartS}
            fullDurationS={playback2.probed_duration_s ?? null}
            subtitle={subtitle}
            onVideoReady={(vid) => { videoElRef.current = vid; }}
            onViewed={() => {
              if (!viewedReported.current) {
                viewedReported.current = true;
                reportEvent("viewed");
              }
            }}
            onTimeUpdate={handleTimeUpdate}
            onEnded={() => {
              if (!completedReported.current) {
                completedReported.current = true;
                reportEvent("completed", Math.floor(lastReportedPos.current));
              }
              if (nextEpisode) setShowAutoAdvance(true);
            }}
          />
          {showAutoAdvance && nextEpisode && (
            <AutoAdvancePrompt
              next={nextEpisode}
              onAdvance={() => {
                nav(`/watch/${encodeURIComponent(nextEpisode.id)}`);
              }}
              onCancel={() => setShowAutoAdvance(false)}
            />
          )}
        </div>
      ) : (
        <DetailHero
          title={item.title}
          subline={item.creator_name ?? item.category ?? item.kind.replace("_", " ")}
          overview={overview}
          backdrop={backdrop}
          poster={poster}
          rating={rating}
          genres={genres}
          runtime={runtime}
          releaseYear={releaseYear}
          resumePosition={resumePosition}
          onPlay={() => void handlePlay()}
          outputTarget={outputTarget}
          casting={casting}
          castTargets={castTargets}
          lastCastTargetId={castTargets ? localStorage.getItem(LAST_CAST_TARGET_KEY) : null}
          onPickCastTarget={handlePickCastTarget}
          onCloseCastPicker={() => setCastTargets(null)}
          isYouTube={isYouTube}
          onOpenYouTube={openOnYouTube}
          validation={validation}
          noStream={!isYouTube && !playback2.stream_url}
          sourcesRetry={!isYouTube && !playback2.stream_url && (playback2.source_count ?? 0) > 0}
          debridError={playback2.debrid_error ?? null}
          onRetrySources={() => void itemQ.refetch()}
        />
      )}

      <div className="mt-6 flex flex-col md:flex-row md:items-start gap-6">
        <div className="flex-1 min-w-0">
          {/* Source attribution / metadata block under the hero */}
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-parchment/80 text-sm">
            <span className="section-label">{item.kind.replace("_", " ")}</span>
            {item.creator_name && <span>{item.creator_name}</span>}
            {item.category && <span>{item.category}</span>}
            {item.language && <span className="uppercase tracking-smallcaps text-xs">{item.language}</span>}
            {directorLine && (
              <span className="text-gold/80">{directorLabel} {directorLine}</span>
            )}
          </div>
          <CastStrip cast={castList} guestStars={guestStars} />
          {playing && overview && (
            <p className="text-parchment mt-4 leading-relaxed max-w-[80ch] whitespace-pre-wrap">{overview}</p>
          )}
        </div>
        <div className="flex flex-col gap-2 w-full md:w-52 shrink-0">
          {isYouTube && (
            <button onClick={openOnYouTube} className="pill-ghost" title="Open on YouTube.com — useful if embed playback is blocked for this video">
              <ExternalLink size={14} /> Open on YouTube
            </button>
          )}
          {nextEpisode && <NextEpisodeButton next={nextEpisode} />}
          {!isYouTube && (
            <SubtitlePicker
              imdbId={subtitleImdb}
              kind={subtitleKind}
              season={subtitleSeason}
              episode={subtitleEpisode}
              selected={subtitle}
              onSelect={setSubtitle}
              originalLanguage={itemQ.data?.item?.original_language ?? null}
              audioLanguage={audioLanguage}
            />
          )}
          {!isYouTube && (
            <SourcePicker
              itemId={decodedId}
              activeSrcId={activeSrcId}
              target={outputTarget === "browser" ? "browser" : "tv"}
              bandwidthSaver={bandwidthSaver}
              onPick={async (resolved) => {
                setPickedSource(resolved);
                // Different release, different track layout — fall back to the
                // new source's own English-first pick.
                setAudioTrack(null);
                // Already watching in the browser: swapping effectivePlayback
                // re-mounts the player on the new url, nothing else to do.
                // Pointed at the TV: push the raw url to mpv so the switch
                // takes effect there too rather than only in this tab.
                if (outputTarget !== "browser") {
                  setCastError(null);
                  try {
                    const res = await Api.cast.mpv({
                      url: resolved.stream_url,
                      sub_file_id: subtitle?.file_id ?? null,
                    });
                    if (!res.ok) throw new Error(res.error ?? "Cast failed");
                  } catch (err) {
                    setCastError((err as Error).message);
                  }
                }
              }}
            />
          )}
          {!isYouTube && effectivePlayback.mode === "exoplayer" && (
            <ForcedSubtitleLoader
              streamUrl={effectivePlayback.stream_url ?? ""}
              audio={activeAudioTrack}
              active={subtitle}
              onLoad={setSubtitle}
            />
          )}
          {!isYouTube && (
            <AudioTrackPicker
              tracks={audioTracks}
              active={activeAudioTrack}
              onSelect={(index) => {
                // Carry the current position across the switch. HLS knows the
                // whole timeline, so the new session can seek straight to it —
                // this is the part the old fragmented-mp4 pipe could not do.
                setAudioSwitchResumeS(Math.floor(lastReportedPos.current) || null);
                setAudioTrack(index);
              }}
            />
          )}
          {needsTranscode && <TranscodeChip codec={audioCodec} />}
          {!isYouTube && outputTarget === "browser" && videoPlayableInBrowser === false && (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-vermilion/15 border border-vermilionHi/60 text-vermilionHi text-xs font-display tracking-smallcaps"
              title={`This release's video is ${videoCodec ?? "an unsupported codec"}. Only the audio is re-encoded — the video is copied through untouched — so a browser without a decoder for it shows a black frame. Cast to the TV, or pick a 1080p x264 release.`}
            >
              ⚠ {videoCodec ?? "video"} — may not decode here
            </span>
          )}
          {!isYouTube && playing && effectivePlayback.mode === "exoplayer" && (
            <TranscodeProgressChip
              streamUrl={effectivePlayback.stream_url ?? ""}
              audio={activeAudioTrack}
            />
          )}
          <OutputPicker value={outputTarget} onChange={setOutputTarget} />
          {!isYouTube && item.kind !== "live_channel" && outputTarget === "browser" && (
            <button
              onClick={toggleBandwidthSaver}
              className={bandwidthSaver ? "pill-ghost text-gold border-gold" : "pill-ghost"}
              title="Bandwidth saver: the server re-encodes to a lighter 1080p stream — about a third of the original bitrate — for connections that stall on the original. Playback continues from the current position when you toggle."
            >
              <Gauge size={14} /> Saver {bandwidthSaver ? "on" : "off"}
            </button>
          )}
          {castError && <p className="text-vermilionHi text-xs">{castError}</p>}
          <BookmarkButton itemId={decodedId} variant="inline" stopPropagation={false} />
          <RecommendButton itemId={decodedId} fromProfileId={profileId} />
          <MoreLikeThis itemId={decodedId} profileId={profileId} />
          <RatingButtons profileId={profileId} itemId={decodedId} />
        </div>
      </div>

      <CommentComposer profileId={profileId} itemId={decodedId} />
    </div>
  );
}

function DetailHero({
  title,
  subline,
  overview,
  backdrop,
  poster,
  rating,
  genres,
  runtime,
  releaseYear,
  resumePosition,
  onPlay,
  outputTarget,
  casting,
  castTargets,
  lastCastTargetId,
  onPickCastTarget,
  onCloseCastPicker,
  isYouTube,
  onOpenYouTube,
  validation,
  noStream,
  sourcesRetry,
  debridError,
  onRetrySources,
}: {
  title: string;
  subline: string | null;
  overview: string | null;
  backdrop: string | null;
  poster: string | null;
  rating: number | null;
  genres: string[];
  runtime: number | null;
  validation?: {
    validated: boolean | null;
    resolved_by: "real-debrid" | "premiumize" | "alldebrid" | "comet" | null;
    probed: number;
    probed_ok: number;
  } | null;
  releaseYear: number | null;
  resumePosition: number;
  onPlay: () => void;
  outputTarget?: OutputTarget;
  casting?: boolean;
  castTargets?: CastDevice[] | null;
  lastCastTargetId?: string | null;
  onPickCastTarget?: (deviceId: string) => void;
  onCloseCastPicker?: () => void;
  isYouTube: boolean;
  onOpenYouTube: () => void;
  noStream?: boolean;
  /** Sources exist but none would mint — show a retry, never "Not available". */
  sourcesRetry?: boolean;
  debridError?: string | null;
  onRetrySources?: () => void;
}): JSX.Element {
  return (
    <div className="rounded-2xl overflow-hidden border border-hairline relative">
      <div className="relative aspect-[16/9] md:aspect-[21/9] bg-lacquer">
        {backdrop && (
          <img
            src={backdrop}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "saturate(0.95) contrast(1.05)" }}
            referrerPolicy="no-referrer"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
        )}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, rgba(15,6,6,0.97) 0%, rgba(15,6,6,0.55) 45%, rgba(26,11,11,0.1) 100%), linear-gradient(to right, rgba(15,6,6,0.7) 0%, rgba(15,6,6,0.0) 50%), radial-gradient(ellipse at 8% 92%, rgba(164,32,45,0.35), transparent 50%), radial-gradient(ellipse at 92% 8%, rgba(199,162,74,0.18), transparent 55%)",
          }}
        />

        {/* Title + meta + Play stacked at the bottom */}
        <div className="absolute inset-0 flex">
          <div className="self-end p-6 md:p-10 max-w-3xl">
            {subline && <div className="section-label text-gold mb-2">{subline}</div>}
            <h1 className="hero-title drop-shadow-lg">{title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-parchment text-sm">
              {rating !== null && (
                <span className="font-display text-goldHi tabular-nums">★ {rating}</span>
              )}
              {releaseYear && (
                <span className="tabular-nums">{releaseYear}</span>
              )}
              {runtime && (
                <span className="tabular-nums">{formatMinutes(runtime)}</span>
              )}
              {genres.length > 0 && (
                <span className="uppercase tracking-smallcaps text-xs">{genres.join(" · ")}</span>
              )}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              {noStream ? (
                sourcesRetry ? (
                  <span className="flex flex-col gap-1.5">
                    <button
                      onClick={onRetrySources}
                      className="pill bg-vermilion border-goldHi text-ivory text-base font-display tracking-smallcaps"
                      style={{ paddingLeft: "1.5rem", paddingRight: "1.5rem" }}
                      title={debridError ?? undefined}
                    >
                      <RotateCcw size={16} /> Retry sources
                    </button>
                    <span className="text-parchment/50 text-xs">
                      {debridError
                        ? "Real-Debrid is refusing right now (it flaps) — retry usually lands."
                        : "Sources exist but none would play yet — worth a retry."}
                    </span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-elev border border-hairline text-parchment/50 text-sm font-display tracking-smallcaps">
                    Not available
                  </span>
                )
              ) : (
                <button
                  onClick={onPlay}
                  disabled={casting}
                  className="pill bg-vermilion border-goldHi text-ivory text-base font-display tracking-smallcaps disabled:opacity-60"
                  style={{ paddingLeft: "1.5rem", paddingRight: "1.5rem" }}
                >
                  {casting ? (
                    <>
                      <Tv2 size={16} className="animate-pulse" /> Sending…
                    </>
                  ) : resumePosition > 0 && !isYouTube && outputTarget !== "wallace-tv" ? (
                    <>
                      <RotateCcw size={16} /> Resume from {fmtTime(resumePosition)}
                    </>
                  ) : resumePosition > 0 && !isYouTube && outputTarget === "wallace-tv" ? (
                    <>
                      <Tv2 size={16} /> Send to TV · {fmtTime(resumePosition)}
                    </>
                  ) : outputTarget === "wallace-tv" ? (
                    <>
                      <Tv2 size={16} /> Send to TV
                    </>
                  ) : (
                    <>
                      <Play size={16} /> Play on Browser
                    </>
                  )}
                </button>
              )}
              {!noStream && resumePosition > 0 && !isYouTube && (
                <button
                  onClick={onPlay}
                  className="pill-ghost border border-hairline"
                  title="Start from the beginning instead"
                >
                  <Play size={14} /> Start over
                </button>
              )}
              {validation && <ValidationChip v={validation} />}
            </div>

            {/* Multi-TV chooser — appears after a Send-to-TV click when the
                server knows more than one receiver. */}
            {castTargets && (
              <div className="mt-4 max-w-sm rounded-xl border border-hairline bg-elev/90 backdrop-blur p-2">
                <div className="flex items-center justify-between px-2 py-1">
                  <span className="section-label text-gold">Send to which TV?</span>
                  <button
                    onClick={onCloseCastPicker}
                    className="text-parchment/60 hover:text-ivory transition-colors"
                    title="Cancel"
                  >
                    <X size={14} />
                  </button>
                </div>
                {castTargets.map((d) => (
                  <button
                    key={d.device_id}
                    onClick={() => onPickCastTarget?.(d.device_id)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-lacquer transition-colors"
                  >
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: d.online ? "#7fb069" : "rgba(199,162,74,0.35)" }}
                    />
                    <span className="font-display text-ivory text-sm">{d.label}</span>
                    {d.device_id === lastCastTargetId && (
                      <span className="text-gold text-[10px] uppercase tracking-smallcaps">last used</span>
                    )}
                    {!d.online && (
                      <span className="ml-auto text-parchment/50 text-xs tabular-nums">
                        {d.last_seen_ago_s < 90
                          ? "just now"
                          : d.last_seen_ago_s < 3600
                            ? `${Math.round(d.last_seen_ago_s / 60)}m ago`
                            : `${Math.round(d.last_seen_ago_s / 3600)}h ago`}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overview text below the backdrop so the description is right there
          without needing to scroll past the player. */}
      {overview && (
        <div className="bg-card/70 backdrop-blur-sm px-6 md:px-10 py-5 border-t border-hairline">
          <p className="text-parchment leading-relaxed max-w-[80ch] whitespace-pre-wrap">
            {overview}
          </p>
        </div>
      )}

      {/* Inline poster thumbnail floats over the bottom-right of the backdrop —
          gives the page a "now playing card" feel even before play. */}
      {poster && (
        <div className="hidden lg:block absolute top-6 right-6 w-32 rounded-md overflow-hidden border border-gold/40 shadow-2xl">
          <img src={poster} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
        </div>
      )}
    </div>
  );
}

function ValidationChip({ v }: { v: {
  validated: boolean | null;
  resolved_by: "real-debrid" | "premiumize" | "alldebrid" | "comet" | null;
  probed: number;
  probed_ok: number;
} }): JSX.Element {
  const providerLabel = v.resolved_by === "real-debrid" ? "Real-Debrid"
    : v.resolved_by === "premiumize" ? "Premiumize"
    : v.resolved_by === "alldebrid" ? "AllDebrid"
    : v.resolved_by === "comet" ? "Comet"
    : null;
  if (v.validated === true) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-jade/20 border border-jade text-jade text-xs font-display tracking-smallcaps"
        title={`${v.probed_ok}/${v.probed} candidate streams validated by HEAD probe`}
      >
        ✓ Validated{providerLabel ? ` · ${providerLabel}` : ""}
      </span>
    );
  }
  if (v.validated === false) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-vermilion/20 border border-vermilionHi text-vermilionHi text-xs font-display tracking-smallcaps"
        title="No candidate streams returned 200/206 in the click-time probe — may not play"
      >
        ⚠ Unverified{providerLabel ? ` · ${providerLabel}` : ""}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-elev border border-hairline text-parchment text-xs font-display tracking-smallcaps">
      Local stream
    </span>
  );
}

function PlayerArea({
  isYouTube,
  ytId,
  playback,
  poster,
  resumePosition,
  fullDurationS,
  subtitle,
  onVideoReady,
  onViewed,
  onTimeUpdate,
  onEnded,
}: {
  isYouTube: boolean;
  ytId: string | null;
  playback: { mode: string; stream_url?: string; external_url?: string };
  poster: string | null;
  resumePosition: number;
  /** Real runtime from the server's probe — lets the player show the full
   *  timeline while a transcode is still growing. */
  fullDurationS?: number | null;
  subtitle: SelectedSubtitle | null;
  onVideoReady: (vid: HTMLVideoElement) => void;
  onViewed: () => void;
  onTimeUpdate: (pos: number, dur: number) => void;
  onEnded: () => void;
}): JSX.Element {
  return (
    <div className="rounded-lg overflow-hidden border border-hairline aspect-video bg-ink">
      {isYouTube && ytId ? (
        <YouTubePlayer
          videoId={ytId}
          startAt={resumePosition}
          onPlay={onViewed}
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
        />
      ) : (
        <HlsPlayer
          src={authenticateStreamUrl(playback.mode === "exoplayer" ? (playback.stream_url ?? "") : "")}
          poster={poster}
          startAt={resumePosition}
          fullDurationS={fullDurationS}
          subtitle={subtitle}
          onReady={(vid) => {
            onVideoReady(vid);
            setTimeout(onViewed, 5000);
          }}
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
          onError={(err) => { console.error("playback error", err); }}
        />
      )}
    </div>
  );
}

function RatingButtons({ profileId, itemId }: { profileId: string; itemId: string }): JSX.Element {
  const [selected, setSelected] = useState<number | null>(null);
  const rate = useMutation({
    mutationFn: (r: number) => Api.library.rate({ profile_id: profileId, item_id: itemId, rating: r }),
  });
  return (
    <details className="card-elev rounded-md">
      <summary className="px-3 py-2 text-sm cursor-pointer text-parchment hover:text-ivory inline-flex items-center gap-2">
        <Star size={14} /> Rate {selected ? `(${selected})` : ""}
      </summary>
      <div className="p-3 grid grid-cols-5 gap-1">
        {[1,2,3,4,5,6,8,9,10].map((n) => (
          <button
            key={n}
            onClick={() => { setSelected(n); rate.mutate(n); }}
            className="w-8 h-8 rounded-full text-sm font-display border border-hairline hover:bg-vermilion hover:border-goldHi text-ivory transition"
          >
            {n}
          </button>
        ))}
      </div>
    </details>
  );
}


function fmtTime(s: number): string {
  if (s < 3600) return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
}
function formatMinutes(n: number): string {
  if (n < 60) return `${n}m`;
  return `${Math.floor(n / 60)}h ${n % 60}m`;
}
